import { PLAN_MATRIX, type Plan } from './entitlements';
import { t } from './translations';

// Montant contractuel négocié (Enterprise « sur devis », Starter/Core remisés…).
// Pur : aucune dépendance réseau — testable et réutilisable côté analytics (MRR).
export type ContractInterval = 'month' | 'year';

export interface Contract {
  amountCents?: number | null;
  currency?: string | null;
  interval?: string | null;
}

const SYMBOLS: Record<string, string> = { eur: '€', usd: '$', gbp: '£', chf: 'CHF', ils: '₪' };
const INTERVALS: Record<string, string> = { month: 'mois', year: 'an' };

/** Symbole si connu, sinon le code en majuscules (SEK, PLN…). */
export function currencySymbol(currency?: string | null): string {
  const c = (currency ?? 'eur').toLowerCase();
  return SYMBOLS[c] ?? c.toUpperCase();
}

/** Montant au format de la langue (`en` → `€895.00 / month`, `he` → `‏895.00 € / חודש`). */
function formatContractAmountLocalized(cents: number, contract: Contract, lang: string): string {
  const interval = (contract.interval ?? 'month').toLowerCase() === 'year' ? 'year' : 'month';
  const code = (contract.currency ?? 'eur').toUpperCase();
  let amount: string;
  try {
    amount = new Intl.NumberFormat(lang, { style: 'currency', currency: code }).format(cents / 100);
  } catch {
    amount = `${new Intl.NumberFormat(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100)} ${currencySymbol(code)}`;
  }
  return `${amount} / ${t(lang, `billing.interval.${interval}`)}`;
}

/**
 * `89500` → `895,00 € / mois`. `null` si aucun montant valide n'est défini.
 * Avec `lang` : montant, devise et période au format de la langue (clés `billing.interval.*`).
 */
export function formatContractAmount(contract?: Contract | null, lang?: string): string | null {
  const cents = contract?.amountCents;
  if (cents == null || !Number.isFinite(cents) || cents < 0) return null;
  if (lang) return formatContractAmountLocalized(cents, contract as Contract, lang);
  const [int, dec] = (cents / 100).toFixed(2).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+$)/g, ' ');
  const interval = INTERVALS[(contract?.interval ?? 'month').toLowerCase()] ?? 'mois';
  return `${grouped},${dec} ${currencySymbol(contract?.currency)} / ${interval}`;
}

/**
 * Libellé de prix à afficher : le montant contractuel gagne sur le tarif public
 * (« Sur devis » pour Enterprise) dès qu'il est renseigné.
 */
export function planPriceLabel(publicPrice: string, contract?: Contract | null, lang?: string): string {
  return formatContractAmount(contract, lang) ?? publicPrice;
}

/** Normalise vers le MRR en centimes (annuel ÷ 12), pour agrégation analytics. */
export function monthlyAmountCents(contract?: Contract | null): number | null {
  const cents = contract?.amountCents;
  if (cents == null || !Number.isFinite(cents) || cents < 0) return null;
  return (contract?.interval ?? 'month').toLowerCase() === 'year' ? Math.round(cents / 12) : cents;
}

// Libellés commerciaux des forfaits — grille v1.6 (deck slides 4-6, #18). Les CODES restent
// stables (freemium/lite/starter/core/enterprise : base, Stripe lookup_keys, overrides) ; seuls
// libellés, prix de référence UE (par utilisateur et par mois, prix fixés par pays) et descriptions
// changent. `annual` = tarif mensuel en facturation annuelle (−20 %), null si sans objet.
// Les descriptions ne citent QUE ce qui existe : rien de la feuille de route (ATS, accès invité…).
export const PLAN_META: Record<Plan, { label: string; price: string; annual: string | null; desc: string }> = {
  freemium:   { label: 'Free',         price: '0 €',                    annual: null,
    desc: 'Découverte, 30 jours par entreprise : 1 fiche de poste, top 20 candidats classés par l’IA, 5 enrichissements et 10 actions d’approche (e-mail) par mois. 1 siège.' },
  lite:       { label: 'Lite',         price: '149 €/utilisateur/mois', annual: '119 €/utilisateur/mois en annuel (−20 %)',
    desc: 'Recruteur solo : recherche IA illimitée, 3 fiches de poste, 50 enrichissements et 50 e-mails par mois, EMILY en brouillons, TrueFit 360 à l’usage. 1 siège.' },
  starter:    { label: 'Professional', price: '395 €/utilisateur/mois', annual: '316 €/utilisateur/mois en annuel (−20 %)',
    desc: 'Recruteurs actifs, équipes jusqu’à 5 sièges : fiches illimitées, e-mail + WhatsApp, 300 enrichissements et 150 actions par siège et par mois, analytics, 5 tests TrueFit inclus.' },
  core:       { label: 'Business',     price: '595 €/utilisateur/mois', annual: '476 €/utilisateur/mois en annuel (−20 %)',
    desc: 'Équipes de recrutement, dès 1 siège : crédits mutualisés (500 enrichissements et 250 actions par siège et par mois), expéditeur WhatsApp dédié, multi-équipes, Deel, placement & outplacement, CSM partagé.' },
  enterprise: { label: 'Enterprise',   price: 'Sur devis',              annual: null,
    desc: 'Grandes organisations, contrat annuel, 10 sièges et plus : volumes sur mesure, compte Twilio propre, MCP (bêta), workflows personnalisés, CSM dédié, SLA et DPA signé.' },
};

/** Forfaits de l'ancien modèle 3 tiers encore présents en base (prod). */
const LEGACY_PLAN_LABELS: Record<string, string> = { startup: 'Startup', professional: 'Professional' };

export function isLegacyPlan(plan: unknown): boolean {
  return typeof plan === 'string' && plan in LEGACY_PLAN_LABELS;
}

export interface PlanMeta { label: string; price: string; annual: string | null; desc: string; legacy: boolean }

// Montants de référence UE (par utilisateur et par mois), alignés sur PLAN_META et sur #18.
const REF_AMOUNT: Partial<Record<Plan, { monthly: number; annual: number }>> = {
  lite: { monthly: 149, annual: 119 }, starter: { monthly: 395, annual: 316 }, core: { monthly: 595, annual: 476 },
};

/** Version traduite (clés `planMeta.*`, 15 langues) d'une entrée de PLAN_META. */
function translatedMeta(plan: Plan, lang: string): Omit<PlanMeta, 'legacy'> {
  const amounts = REF_AMOUNT[plan];
  return {
    label: t(lang, `planMeta.${plan}.label`),
    price: amounts ? t(lang, 'planMeta.priceFmt').replace('{n}', String(amounts.monthly))
      : t(lang, plan === 'enterprise' ? 'planMeta.onQuote' : 'planMeta.freePrice'),
    annual: amounts ? t(lang, 'planMeta.annualFmt').replace('{n}', String(amounts.annual)) : null,
    desc: t(lang, `planMeta.${plan}.desc`),
  };
}

/**
 * Descriptif d'affichage d'un forfait, **jamais undefined**. Une valeur hors des 5 tiers
 * (`startup`/`professional` hérités du modèle 3 tiers, ou n'importe quoi d'autre) renvoie un
 * descriptif « hérité » : l'accès réel est celui de `normalizePlan()` (fail-closed → freemium, #13),
 * et l'admin doit choisir explicitement le forfait cible.
 * Sans `lang` : français (PLAN_META) ; avec `lang` : textes via t() (clés `planMeta.*`).
 */
export function planMetaFor(plan: unknown, lang?: string): PlanMeta {
  const known = (PLAN_MATRIX as Record<string, unknown>)[plan as string] ? PLAN_META[plan as Plan] : undefined;
  if (known) return { ...(lang ? translatedMeta(plan as Plan, lang) : known), legacy: false };
  const name = isLegacyPlan(plan) ? LEGACY_PLAN_LABELS[plan as string] : String(plan ?? '—');
  if (lang) {
    return {
      label: t(lang, 'planMeta.legacyLabelFmt').replace('{name}', name),
      price: t(lang, 'planMeta.legacyPrice'),
      annual: null,
      desc: t(lang, 'planMeta.legacyDesc'),
      legacy: true,
    };
  }
  return {
    label: `${name} (hérité)`,
    price: 'Forfait hérité — choisir un tarif',
    annual: null,
    desc: "Valeur de l'ancien modèle 3 tiers. L'accès est résolu comme « Free » (fail-closed, #13) tant qu'un forfait du catalogue n'est pas sélectionné et enregistré.",
    legacy: true,
  };
}
